import React from 'react';
import { Spinner } from '@/components/ui';
import type { PcrMetricsResponse } from '../../../services/pcrService';

interface PcrMetricsSummaryCardProps {
  metrics: PcrMetricsResponse | null;
  loading?: boolean;
  className?: string;
}

// Summary Totals as a compact, fixed-height strip (W-iP4).
//
// It used to be a tall card (a padded header bar, then a 4-tile grid when expanded) sitting
// between the filters and the table. On an iPad, expanded, it took most of the height the table
// needed, which is part of why only one row of PCRs was visible. Four counts fit on one line, so
// they are now always visible and never expand: the strip is the height of one line of chips and
// wraps to a second line only on a phone.
const TILES: Array<{
  key: 'new' | 'internal_review' | 'finalized' | 'total';
  label: string;
  icon: string;
  tone: string;
}> = [
  { key: 'new', label: 'New', icon: 'bi-envelope-open', tone: 'tw-bg-warning/10 tw-text-warning-text' },
  { key: 'internal_review', label: 'Internal Review', icon: 'bi-hourglass-split', tone: 'tw-bg-info/10 tw-text-info' },
  { key: 'finalized', label: 'Finalized', icon: 'bi-check-circle', tone: 'tw-bg-success/10 tw-text-success' },
  { key: 'total', label: 'Total', icon: 'bi-collection', tone: 'tw-bg-secondary/10 tw-text-secondary' },
];

const PcrMetricsSummaryCard: React.FC<PcrMetricsSummaryCardProps> = ({ metrics, loading = false, className = '' }) => (
  <section
    aria-label="Summary Totals"
    data-testid="pcr-summary-totals"
    title="Totals use the same filters as the table (including status) and count every matching row, not only this page."
    className={`tw-mb-3 tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-rounded-lg tw-border tw-border-line-input tw-bg-surface tw-px-3 tw-py-2 tw-text-sm ${className}`}
  >
    <span className="tw-mr-1 tw-inline-flex tw-items-center tw-font-semibold tw-text-content">
      <i className="bi bi-graph-up tw-mr-2 tw-text-primary" aria-hidden="true" />
      Summary Totals
    </span>
    {loading && !metrics ? (
      <Spinner animation="border" size="sm" role="status">
        Loading...
      </Spinner>
    ) : (
      TILES.map((t) => (
        <span
          key={t.key}
          data-metric={t.key}
          className={`tw-inline-flex tw-items-center tw-gap-1.5 tw-whitespace-nowrap tw-rounded-full tw-px-2.5 tw-py-0.5 ${t.tone}`}
        >
          <i className={`bi ${t.icon}`} aria-hidden="true" />
          <span>{t.label}</span>
          <span className="tw-font-semibold tw-tabular-nums">{metrics?.[t.key] ?? 0}</span>
        </span>
      ))
    )}
  </section>
);

export default PcrMetricsSummaryCard;
